import { useState, useEffect } from "react";

import { IFilterDataItem } from "components/Filters/filtersOptions";
import {
  hasChangedSavedValues,
  hasSelectedFilters,
  FILTER_DATA_ITEMS_ARRAY,
  NUMBER,
  PRIMITIVES_ARRAY
} from "components/Filters/helper";
import { useReduxStateFilters } from "./useReduxStateFilters";
import {
  initialSliderFilterValues,
  MAX_SLIDER_FILTER_VALUE,
  MIN_SLIDER_FILTER_VALUE
} from "./WorkOrdersFiltersDialog/filterOptions";

export interface IOpenFiltersState {
  hasSelectedOpenFilters: boolean;
  hasChangedSavedOpenValues: boolean;
  openDate: number;
  setOpenDate: (value: number) => void;
  timeOpen: number[];
  setTimeOpen: (value: number[]) => void;
  openOrderTypes: string[];
  setOpenOrderTypes: (value: string[]) => void;
  openReporters: IFilterDataItem[];
  setOpenReporters: (value: IFilterDataItem[]) => void;
}

export interface IAssignedFiltersState {
  hasSelectedAssignedFilters: boolean;
  hasChangedSavedAssignedValues: boolean;
  assignedDate: number;
  setAssignedDate: (value: number) => void;
  timeAssigned: number[];
  setTimeAssigned: (value: number[]) => void;
  assignedTo: string[];
  setAssignedTo: (value: string[]) => void;
  assignedOrderTypes: string[];
  setAssignedOrderTypes: (value: string[]) => void;
  assignedReporters: IFilterDataItem[];
  setAssignedReporters: (value: IFilterDataItem[]) => void;
}

export interface ICompleteFiltersState {
  hasSelectedCompleteFilters: boolean;
  hasChangedSavedCompleteValues: boolean;
  completeAssignedDate: number;
  setCompleteAssignedDate: (value: number) => void;
  completeTimeAssigned: number[];
  setCompleteTimeAssigned: (value: number[]) => void;
  completedDate: number;
  setCompletedDate: (value: number) => void;
  completeAssignedTo: string[];
  setCompleteAssignedTo: (value: string[]) => void;
  completeOrderTypes: string[];
  setCompleteOrderTypes: (value: string[]) => void;
}

export interface IClosedFiltersState {
  hasSelectedClosedFilters: boolean;
  hasChangedSavedClosedValues: boolean;
  closedDate: number;
  setClosedDate: (value: number) => void;
  closedTimeOpen: number[];
  setClosedTimeOpen: (value: number[]) => void;
  closedTimeAssigned: number[];
  setClosedTimeAssigned: (value: number[]) => void;
  closedAssignedTo: string[];
  setClosedAssignedTo: (value: string[]) => void;
  closedOrderTypes: string[];
  setClosedOrderTypes: (value: string[]) => void;
  closedReporters: IFilterDataItem[];
  setClosedReporters: (value: IFilterDataItem[]) => void;
}

export interface ICanceledFiltersState {
  hasSelectedCanceledFilters: boolean;
  hasChangedSavedCanceledValues: boolean;
  canceledOpenDate: number;
  setCanceledOpenDate: (value: number) => void;
  canceledDate: number;
  setCanceledDate: (value: number) => void;
  canceledTimeAssigned: number[];
  setCanceledTimeAssigned: (value: number[]) => void;
  canceledAssignedTo: string[];
  setCanceledAssignedTo: (value: string[]) => void;
  canceledOrderTypes: string[];
  setCanceledOrderTypes: (value: string[]) => void;
  canceledReporters: IFilterDataItem[];
  setCanceledReporters: (value: IFilterDataItem[]) => void;
}

export interface ILocalStateFilters {
  openFiltersState: IOpenFiltersState;
  assignedFiltersState: IAssignedFiltersState;
  completeFiltersState: ICompleteFiltersState;
  closedFiltersState: IClosedFiltersState;
  canceledFiltersState: ICanceledFiltersState;
}

export const useLocalStateFilters = (): ILocalStateFilters => {
  const {
    openFilters,
    assignedFilters,
    completeFilters,
    closedFilters,
    canceledFilters
  } = useReduxStateFilters();

  // Open Tab Filters
  const [openDate, setOpenDate] = useState<number>(0);
  const [openOrderTypes, setOpenOrderTypes] = useState<string[]>(null);
  const [openReporters, setOpenReporters] = useState<IFilterDataItem[]>([]);
  const [timeOpen, setTimeOpen] = useState<number[]>(initialSliderFilterValues);

  const hasSelectedOpenFilters = hasSelectedFilters(
    [openDate, openOrderTypes, openReporters, timeOpen],
    MIN_SLIDER_FILTER_VALUE,
    MAX_SLIDER_FILTER_VALUE
  );

  const hasChangedSavedOpenValues = [
    hasChangedSavedValues(openDate, openFilters?.openDate, NUMBER),
    hasChangedSavedValues(
      openOrderTypes,
      openFilters?.orderTypes,
      PRIMITIVES_ARRAY
    ),
    hasChangedSavedValues(
      openReporters,
      openFilters?.reporters,
      FILTER_DATA_ITEMS_ARRAY
    ),
    hasChangedSavedValues(timeOpen, openFilters?.timeOpen, PRIMITIVES_ARRAY)
  ].some(item => item);

  useEffect(() => {
    setOpenDate(openFilters?.openDate || 0);
    setOpenOrderTypes(openFilters?.orderTypes || null);
    setOpenReporters(openFilters?.reporters || []);
    setTimeOpen(openFilters?.timeOpen || initialSliderFilterValues);
  }, [openFilters]);

  // Assigned Tab Filters
  const [assignedDate, setAssignedDate] = useState<number>(0);
  const [assignedTo, setAssignedTo] = useState<string[]>(null);
  const [assignedOrderTypes, setAssignedOrderTypes] = useState<string[]>(null);
  const [assignedReporters, setAssignedReporters] = useState<IFilterDataItem[]>(
    []
  );
  const [timeAssigned, setTimeAssigned] = useState<number[]>(
    initialSliderFilterValues
  );

  const hasSelectedAssignedFilters = hasSelectedFilters(
    [
      assignedDate,
      assignedTo,
      assignedOrderTypes,
      assignedReporters,
      timeAssigned
    ],
    MIN_SLIDER_FILTER_VALUE,
    MAX_SLIDER_FILTER_VALUE
  );

  const hasChangedSavedAssignedValues = [
    hasChangedSavedValues(assignedDate, assignedFilters?.assignedDate, NUMBER),
    hasChangedSavedValues(
      assignedTo,
      assignedFilters?.assignedTo,
      PRIMITIVES_ARRAY
    ),
    hasChangedSavedValues(
      assignedOrderTypes,
      assignedFilters?.orderTypes,
      PRIMITIVES_ARRAY
    ),
    hasChangedSavedValues(
      assignedReporters,
      assignedFilters?.reporters,
      FILTER_DATA_ITEMS_ARRAY
    ),
    hasChangedSavedValues(
      timeAssigned,
      assignedFilters?.timeAssigned,
      PRIMITIVES_ARRAY
    )
  ].some(item => item);

  useEffect(() => {
    setAssignedDate(assignedFilters?.assignedDate || 0);
    setAssignedTo(assignedFilters?.assignedTo || null);
    setAssignedOrderTypes(assignedFilters?.orderTypes || null);
    setAssignedReporters(assignedFilters?.reporters || []);
    setTimeAssigned(assignedFilters?.timeAssigned || initialSliderFilterValues);
  }, [assignedFilters]);

  // Complete Tab Filters
  const [completeAssignedDate, setCompleteAssignedDate] = useState<number>(0);
  const [completedDate, setCompletedDate] = useState<number>(0);
  const [completeAssignedTo, setCompleteAssignedTo] = useState<string[]>(null);
  const [completeOrderTypes, setCompleteOrderTypes] = useState<string[]>(null);
  const [completeTimeAssigned, setCompleteTimeAssigned] = useState<number[]>(
    initialSliderFilterValues
  );

  const hasSelectedCompleteFilters = hasSelectedFilters(
    [
      completeAssignedDate,
      completedDate,
      completeAssignedTo,
      completeOrderTypes,
      completeTimeAssigned
    ],
    MIN_SLIDER_FILTER_VALUE,
    MAX_SLIDER_FILTER_VALUE
  );

  const hasChangedSavedCompleteValues = [
    hasChangedSavedValues(
      completeAssignedDate,
      completeFilters?.assignedDate,
      NUMBER
    ),
    hasChangedSavedValues(
      completedDate,
      completeFilters?.completedDate,
      NUMBER
    ),
    hasChangedSavedValues(
      completeAssignedTo,
      completeFilters?.assignedTo,
      PRIMITIVES_ARRAY
    ),
    hasChangedSavedValues(
      completeOrderTypes,
      completeFilters?.orderTypes,
      PRIMITIVES_ARRAY
    ),
    hasChangedSavedValues(
      completeTimeAssigned,
      completeFilters?.timeAssigned,
      PRIMITIVES_ARRAY
    )
  ].some(item => item);

  useEffect(() => {
    setCompleteAssignedDate(completeFilters?.assignedDate || 0);
    setCompletedDate(completeFilters?.completedDate || 0);
    setCompleteAssignedTo(completeFilters?.assignedTo || null);
    setCompleteOrderTypes(completeFilters?.orderTypes || null);
    setCompleteTimeAssigned(
      completeFilters?.timeAssigned || initialSliderFilterValues
    );
  }, [completeFilters]);

  // Closed Tab Filters
  const [closedAssignedTo, setClosedAssignedTo] = useState<string[]>(null);
  const [closedDate, setClosedDate] = useState<number>(0);
  const [closedOrderTypes, setClosedOrderTypes] = useState<string[]>(null);
  const [closedReporters, setClosedReporters] = useState<IFilterDataItem[]>([]);
  const [closedTimeOpen, setClosedTimeOpen] = useState<number[]>(
    initialSliderFilterValues
  );
  const [closedTimeAssigned, setClosedTimeAssigned] = useState<number[]>(
    initialSliderFilterValues
  );

  const hasSelectedClosedFilters = hasSelectedFilters(
    [
      closedDate,
      closedAssignedTo,
      closedOrderTypes,
      closedReporters,
      closedTimeOpen,
      closedTimeAssigned
    ],
    MIN_SLIDER_FILTER_VALUE,
    MAX_SLIDER_FILTER_VALUE
  );

  const hasChangedSavedClosedValues = [
    hasChangedSavedValues(closedDate, closedFilters?.closedDate, NUMBER),
    hasChangedSavedValues(
      closedAssignedTo,
      closedFilters?.assignedTo,
      PRIMITIVES_ARRAY
    ),
    hasChangedSavedValues(
      closedOrderTypes,
      closedFilters?.orderTypes,
      PRIMITIVES_ARRAY
    ),
    hasChangedSavedValues(
      closedReporters,
      closedFilters?.reporters,
      FILTER_DATA_ITEMS_ARRAY
    ),
    hasChangedSavedValues(
      closedTimeOpen,
      closedFilters?.timeOpen,
      PRIMITIVES_ARRAY
    ),
    hasChangedSavedValues(
      closedTimeAssigned,
      closedFilters?.timeAssigned,
      PRIMITIVES_ARRAY
    )
  ].some(item => item);

  useEffect(() => {
    setClosedDate(closedFilters?.closedDate || 0);
    setClosedAssignedTo(closedFilters?.assignedTo || null);
    setClosedOrderTypes(closedFilters?.orderTypes || null);
    setClosedReporters(closedFilters?.reporters || []);
    setClosedTimeOpen(closedFilters?.timeOpen || initialSliderFilterValues);
    setClosedTimeAssigned(
      closedFilters?.timeAssigned || initialSliderFilterValues
    );
  }, [closedFilters]);

  //Canceled Tab Filters
  const [canceledReporters, setCanceledReporters] = useState<IFilterDataItem[]>(
    []
  );
  const [canceledOpenDate, setCanceledOpenDate] = useState<number>(0);
  const [canceledAssignedTo, setCanceledAssignedTo] = useState<string[]>(null);
  const [canceledDate, setCanceledDate] = useState<number>(0);
  const [canceledOrderTypes, setCanceledOrderTypes] = useState<string[]>(null);
  const [canceledTimeAssigned, setCanceledTimeAssigned] = useState<number[]>(
    initialSliderFilterValues
  );

  const hasSelectedCanceledFilters = hasSelectedFilters(
    [
      canceledReporters,
      canceledOpenDate,
      canceledAssignedTo,
      canceledDate,
      canceledOrderTypes,
      canceledTimeAssigned
    ],
    MIN_SLIDER_FILTER_VALUE,
    MAX_SLIDER_FILTER_VALUE
  );

  const hasChangedSavedCanceledValues = [
    hasChangedSavedValues(canceledOpenDate, canceledFilters?.openDate, NUMBER),
    hasChangedSavedValues(canceledDate, canceledFilters?.canceledDate, NUMBER),
    hasChangedSavedValues(
      canceledAssignedTo,
      canceledFilters?.assignedTo,
      PRIMITIVES_ARRAY
    ),
    hasChangedSavedValues(
      canceledOrderTypes,
      canceledFilters?.orderTypes,
      PRIMITIVES_ARRAY
    ),
    hasChangedSavedValues(
      canceledReporters,
      canceledFilters?.reporters,
      FILTER_DATA_ITEMS_ARRAY
    ),
    hasChangedSavedValues(
      canceledTimeAssigned,
      canceledFilters?.timeAssigned,
      PRIMITIVES_ARRAY
    )
  ].some(item => item);

  useEffect(() => {
    setCanceledReporters(canceledFilters?.reporters || []);
    setCanceledOpenDate(canceledFilters?.openDate || 0);
    setCanceledAssignedTo(canceledFilters?.assignedTo || null);
    setCanceledDate(canceledFilters?.canceledDate || 0);
    setCanceledOrderTypes(canceledFilters?.orderTypes || null);
    setCanceledTimeAssigned(
      canceledFilters?.timeAssigned || initialSliderFilterValues
    );
  }, [canceledFilters]);

  return {
    openFiltersState: {
      hasSelectedOpenFilters,
      hasChangedSavedOpenValues,
      openDate,
      setOpenDate,
      openOrderTypes,
      setOpenOrderTypes,
      openReporters,
      setOpenReporters,
      timeOpen,
      setTimeOpen
    },
    assignedFiltersState: {
      hasSelectedAssignedFilters,
      hasChangedSavedAssignedValues,
      assignedDate,
      setAssignedDate,
      assignedTo,
      setAssignedTo,
      assignedOrderTypes,
      setAssignedOrderTypes,
      assignedReporters,
      setAssignedReporters,
      timeAssigned,
      setTimeAssigned
    },
    completeFiltersState: {
      hasSelectedCompleteFilters,
      hasChangedSavedCompleteValues,
      completeAssignedDate,
      setCompleteAssignedDate,
      completedDate,
      setCompletedDate,
      completeAssignedTo,
      setCompleteAssignedTo,
      completeOrderTypes,
      setCompleteOrderTypes,
      completeTimeAssigned,
      setCompleteTimeAssigned
    },
    closedFiltersState: {
      hasSelectedClosedFilters,
      hasChangedSavedClosedValues,
      closedDate,
      setClosedDate,
      closedAssignedTo,
      setClosedAssignedTo,
      closedOrderTypes,
      setClosedOrderTypes,
      closedReporters,
      setClosedReporters,
      closedTimeOpen,
      setClosedTimeOpen,
      closedTimeAssigned,
      setClosedTimeAssigned
    },
    canceledFiltersState: {
      hasSelectedCanceledFilters,
      hasChangedSavedCanceledValues,
      canceledReporters,
      setCanceledReporters,
      canceledOpenDate,
      setCanceledOpenDate,
      canceledAssignedTo,
      setCanceledAssignedTo,
      canceledDate,
      setCanceledDate,
      canceledOrderTypes,
      setCanceledOrderTypes,
      canceledTimeAssigned,
      setCanceledTimeAssigned
    }
  };
};
